import { navigate } from "gatsby";
import * as React from "react";
import { CoinListItemResult } from "../../../../../types/api-results/coin-list-item";
import { useExchangeList } from "../../../../hooks/query-hooks/use-exchange-list";
import { SiteContext } from "../../../../site-context/site-context";
import { EventCategory } from "../../../../utils/analytics";
import { buildExchangeUrl } from "../../../../utils/build-url";
import { formatCurrency } from "../../../../utils/format-number";
import Actionable from "../../../ui-library/actionable/actionable";
import Badge from "../../../ui-library/badge/badge";
import { Card } from "../../../ui-library/cards/card";
import { Colors } from "../../../ui-library/design-tokens/colors";
import Image from "../../../ui-library/image/image";
import LoadingState from "../../../ui-library/loading-state/loading-state";
import { Table, TBody, THead } from "../../../ui-library/table/table";
import styles from "./exchange-list-table.styles";

interface ExchangeListTableProps {
  isDex?: boolean;
  staticData?: CoinListItemResult[];
}

const ExchangeListTable: React.FC<ExchangeListTableProps> = ({
  isDex,
  staticData,
}) => {
  const {
    currency: [currency],
    btcExchangeRate,
    isMobile,
  } = React.useContext(SiteContext);

  const { data, liveDataInitialLoading } = useExchangeList({
    staticData,
    size: 150,
    orderBy: isDex ? "volume" : "trust_score",
    isDex,
  });

  const rankedData = data?.map((exchange, index) => ({
    ...exchange,
    rank: index + 1,
  }));

  const eventCategory = EventCategory.ExchangeList;

  return (
    <Card>
      {liveDataInitialLoading && <LoadingState cover={true} />}

      <Table>
        <THead>
          {!isMobile && <th css={styles.colAlignment[0]}>#</th>}
          <th css={styles.colAlignment[1]}>Name</th>
          <th css={styles.colAlignment[2]}>24h Volume</th>
          <th css={styles.colAlignment[3]}>Trust score</th>
        </THead>
        <TBody>
          {rankedData?.map((row) => (
            <Actionable
              key={row.id}
              component="tr"
              role="link"
              ariaLabel={row.name}
              css={styles.row}
              onClick={() => navigate(buildExchangeUrl(row.slug))}
              analytics={{
                category: eventCategory,
                action: "Click - Exchange table row",
                label: row.name,
              }}
            >
              {!isMobile && (
                <td css={[styles.colAlignment[0], styles.rank]}>{row.rank}</td>
              )}
              <td css={[styles.colAlignment[1]]}>
                <div css={styles.nameContainer}>
                  <div css={styles.nameColLeft}>
                    <div css={styles.imageContainer}>
                      <Image src={row.image} css={styles.image} alt="logo" />
                    </div>
                    <div css={styles.nameColRank}>{row.rank}</div>
                  </div>
                  <div>
                    <a
                      css={styles.name}
                      href={buildExchangeUrl(row.slug)}
                      onClick={(e) => e.preventDefault()}
                    >
                      {row.name}
                    </a>
                    <div css={styles.symbol}>
                      {row.isDex ? (
                        <Badge>DEX</Badge>
                      ) : (
                        <Badge color={Colors.Orange}>CEX</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </td>
              <td css={[styles.colAlignment[2], styles.label]}>
                {row.tradeVolume24hBtc
                  ? formatCurrency({
                      value: row.tradeVolume24hBtc * (btcExchangeRate || 1),
                      currency,
                    })
                  : "-"}
              </td>
              <td css={[styles.colAlignment[3], styles.label]}>
                {row.trustScore || "-"}
              </td>
            </Actionable>
          ))}
        </TBody>
      </Table>
    </Card>
  );
};

export default ExchangeListTable;
